package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.openFileInBrowser
import br.com.vvrefeicoes.portal.core.util.saveFile
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewFilters
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewItem
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewSummary
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewTotals
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewView
import br.com.vvrefeicoes.portal.domain.model.RmpRecordAttachment
import br.com.vvrefeicoes.portal.domain.model.RmpReportKind
import br.com.vvrefeicoes.portal.domain.model.RmpSyncResult
import br.com.vvrefeicoes.portal.domain.repository.RmpRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

class RmpController(
    private val repository: RmpRepository,
    private val scope: CoroutineScope,
) {
    var loading by mutableStateOf(true)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var summary by mutableStateOf(RmpOverviewSummary())
        private set

    var items by mutableStateOf<List<RmpOverviewItem>>(emptyList())
        private set

    var query by mutableStateOf("")
        private set

    /** Aba atual: Em andamento (onde se aprova), Histórico de unidade ou Histórico de usuário. */
    var view by mutableStateOf(RmpOverviewView.ANDAMENTO)
        private set

    /** Filtros de tipo, unidade, período e etapa (o dashboard do sistema antigo). */
    var filters by mutableStateOf(RmpOverviewFilters())
        private set

    /** Totais por status e tipo da lista filtrada e as opções dos filtros. */
    var totals by mutableStateOf(RmpOverviewTotals())
        private set

    /** Filtro "Minhas pendências", só na aba Em andamento. */
    var myPendingOnly by mutableStateOf(false)
        private set

    var page by mutableStateOf(0)
        private set

    var detailsItem by mutableStateOf<RmpOverviewItem?>(null)
        private set

    /** Anexos do RMP antigo aberto em "Detalhes" (os do portal ficam no detalhe de aprovação). */
    var detailsAttachments by mutableStateOf<List<RmpRecordAttachment>>(emptyList())
        private set

    var detailsAttachmentsLoading by mutableStateOf(false)
        private set

    var detailsAttachmentsError by mutableStateOf<String?>(null)
        private set

    private var detailsJob: Job? = null

    /** RMP do portal com a confirmação de Aprovar/Rejeitar aberta direto da linha da tabela. */
    var decisionItem by mutableStateOf<RmpOverviewItem?>(null)
        private set

    var decisionApprove by mutableStateOf(true)
        private set

    var exporting by mutableStateOf(false)
        private set

    var exportError by mutableStateOf<String?>(null)
        private set

    var syncing by mutableStateOf(false)
        private set

    var syncResult by mutableStateOf<RmpSyncResult?>(null)
        private set

    var syncError by mutableStateOf<String?>(null)
        private set

    /** A API informa se o fluxo nativo (Solicitar RMP) está ligado; desligado, o RMP oficial é o sistema antigo. */
    var requestsEnabled by mutableStateOf(false)
        private set

    val pageSize = 20

    val pageCount: Int
        get() = maxOf(1, totalPages)

    private var totalPages by mutableStateOf(1)
    private var loadJob: Job? = null
    private var initialized = false

    fun init() {
        if (initialized) return
        initialized = true
        requestPage()
    }

    fun retry() {
        errorMessage = null
        requestPage()
    }

    /** A busca espera o usuário parar de digitar antes de ir ao servidor. */
    fun updateQuery(value: String) {
        if (value == query) return
        query = value
        page = 0
        requestPage(debounce = true)
    }

    fun updateMyPendingOnly(value: Boolean) {
        if (value == myPendingOnly) return
        myPendingOnly = value
        page = 0
        requestPage()
    }

    fun updateView(value: RmpOverviewView) {
        if (value == view) return
        view = value
        page = 0
        requestPage()
    }

    private val listMyPendingOnly: Boolean
        get() = myPendingOnly && view == RmpOverviewView.ANDAMENTO

    fun goToPage(value: Int) {
        val target = value.coerceIn(0, pageCount - 1)
        if (target == page) return
        page = target
        requestPage()
    }

    fun openDetails(item: RmpOverviewItem) {
        detailsItem = item
        detailsAttachments = emptyList()
        detailsAttachmentsError = null
        if (!item.isPortal) loadDetailsAttachments(item.id)
    }

    fun dismissDetails() {
        detailsJob?.cancel()
        detailsItem = null
        detailsAttachments = emptyList()
        detailsAttachmentsError = null
    }

    /** Baixa um anexo do RMP antigo já copiado para o S3 do portal. */
    fun downloadAttachment(attachment: RmpRecordAttachment) {
        val item = detailsItem ?: return
        if (!attachment.available) return
        scope.launch {
            try {
                val bytes = repository.downloadRecordAttachment(item.id, attachment.id)
                if (!saveFile(attachment.fileName, attachment.contentType ?: DEFAULT_MIME_TYPE, bytes)) {
                    detailsAttachmentsError = "Não foi possível baixar o arquivo neste dispositivo."
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                detailsAttachmentsError = throwable.message ?: "Não foi possível baixar o arquivo."
            }
        }
    }

    private fun loadDetailsAttachments(rmpRecordId: String) {
        detailsJob?.cancel()
        detailsAttachmentsLoading = true
        detailsJob =
            scope.launch {
                try {
                    detailsAttachments = repository.loadRecordAttachments(rmpRecordId)
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    detailsAttachmentsError = throwable.message ?: "Não foi possível carregar os anexos."
                } finally {
                    detailsAttachmentsLoading = false
                }
            }
    }

    fun openDecision(
        item: RmpOverviewItem,
        approve: Boolean,
    ) {
        decisionItem = item
        decisionApprove = approve
    }

    fun dismissDecision() {
        decisionItem = null
    }

    /** Depois de aprovar ou rejeitar um RMP do portal: a lista e os cartões mudam (etapa, status, pendências). */
    fun refresh() {
        requestPage()
    }

    fun export() {
        if (exporting) return
        exporting = true
        exportError = null
        scope.launch {
            try {
                val bytes = repository.exportOverview(query, view, listMyPendingOnly, filters)
                if (!saveFile("rmp.csv", "text/csv;charset=utf-8", bytes)) {
                    exportError = "Não foi possível baixar o arquivo neste dispositivo."
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                exportError = throwable.message ?: "Não foi possível exportar os dados."
            } finally {
                exporting = false
            }
        }
    }

    fun updateFilters(value: RmpOverviewFilters) {
        if (value == filters) return
        filters = value
        page = 0
        requestPage()
    }

    fun clearFilters() = updateFilters(RmpOverviewFilters())

    /** Baixa a planilha do relatório escolhido com a lista atual. */
    fun exportReport(kind: RmpReportKind) {
        if (exporting) return
        exporting = true
        exportError = null
        scope.launch {
            try {
                val bytes = repository.exportReport(kind, query, view, listMyPendingOnly, filters)
                val name = "rmp_${kind.name.lowercase()}.xlsx"
                if (!saveFile(name, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", bytes)) {
                    exportError = "Não foi possível baixar o arquivo neste dispositivo."
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                exportError = throwable.message ?: "Não foi possível gerar o relatório."
            } finally {
                exporting = false
            }
        }
    }

    /** Abre a lista atual pronta para imprimir ou salvar em PDF. */
    fun printList() {
        if (exporting) return
        exporting = true
        exportError = null
        scope.launch {
            try {
                val bytes = repository.printOverview(query, view, listMyPendingOnly, filters)
                if (!openFileInBrowser("rmp-lista.html", "text/html", bytes)) {
                    exportError = "Não foi possível abrir a lista neste dispositivo."
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                exportError = throwable.message ?: "Não foi possível gerar a lista."
            } finally {
                exporting = false
            }
        }
    }

    fun dismissExportError() {
        exportError = null
    }

    fun sync() {
        if (syncing) return
        syncing = true
        syncResult = null
        syncError = null
        scope.launch {
            try {
                syncResult = repository.sync()
                page = 0
                requestPage()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                syncError = throwable.message ?: "Não foi possível sincronizar com a RMP."
            } finally {
                syncing = false
            }
        }
    }

    fun dismissSyncFeedback() {
        syncResult = null
        syncError = null
    }

    private fun requestPage(debounce: Boolean = false) {
        loadJob?.cancel()
        loadJob =
            scope.launch {
                if (debounce) delay(SEARCH_DEBOUNCE_MS)
                loadPage()
            }
    }

    private suspend fun loadPage() {
        loading = true
        errorMessage = null
        try {
            val result = repository.loadOverview(query, view, listMyPendingOnly, page, pageSize, filters)
            summary = result.summary
            totals = result.totals
            items = result.items
            totalPages = result.totalPages
            requestsEnabled = result.requestsEnabled
        } catch (exception: CancellationException) {
            throw exception
        } catch (throwable: Throwable) {
            items = emptyList()
            totalPages = 1
            errorMessage = throwable.message ?: "Não foi possível carregar os formulários da RMP."
        } finally {
            loading = false
        }
    }

    private companion object {
        const val SEARCH_DEBOUNCE_MS = 350L
        const val DEFAULT_MIME_TYPE = "application/octet-stream"
    }
}

@Composable
fun rememberRmpController(): RmpController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { RmpController(appGraph.rmpRepository, scope) }
}
